import React, { useState, useEffect, useDeferredValue } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  useMediaQuery,
  Chip,
  Pagination,
  InputBase,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTheme } from "../../../../contexts/ThemeContext";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExportDropdown from "../../../../components/ExportDropdown";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useTranslation } from "react-i18next";
import TruncatedCell from "../../../../components/TruncatedCell.js";
import TaskAttendanceHistoryFilterDrawer from "./FilterDrawer.js";
import empty from "../../../../assets/empty.png";
import { useCompanyProfile } from "../../../../components/CompanyProfile";
import ExportTaskAttendanceHistoryExcel from "./HistoryExcel.js";
import ExportTaskAttendanceHistoryPDF from "./HistoryPdf.js";
import { pdf } from "@react-pdf/renderer";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";

/** Page size for list API — must stay in sync with backend `limit` query. */
const ROWS_PER_PAGE = 10;

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_ONLY_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

/** Prefer UTC instants so the local calendar day matches scheduled/actual times. */
const resolveTaskDateInstant = (task, dateGroupDate) =>
  task?.scheduled?.start_at ||
  task?.actual?.started_at ||
  dateGroupDate ||
  task?.task_date;

/** Date-only API values must include time so dateTimeUtils treats them as UTC. */
const ensureUtcParseableInstant = (value) => {
  if (value == null || value === "") return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (DATE_ONLY_REGEX.test(trimmed)) return `${trimmed}T00:00:00`;
  return trimmed;
};

const isTimeOnlyLocalClock = (value) => {
  const trimmed = String(value).trim();
  return (
    TIME_ONLY_REGEX.test(trimmed) &&
    !trimmed.includes("T") &&
    !trimmed.includes("-")
  );
};

/**
 * API shape (Postman): `data.pagination` with `page`, `limit`, `total`, `pages`, `has_next`, `has_prev`.
 * Also accepts `pagination` nested under `data.data` if the envelope differs.
 */
function getPaginationFromResponse(payload) {
  return payload?.data?.pagination ?? payload?.data?.data?.pagination ?? payload?.pagination ?? null;
}

function resolveTotalPageCount(paginationMeta, requestLimit) {
  if (!paginationMeta || typeof paginationMeta !== "object") return 1;
  const explicit = paginationMeta.pages ?? paginationMeta.totalPages;
  if (explicit != null && Number(explicit) >= 1) return Number(explicit);
  const total = paginationMeta.total != null ? Number(paginationMeta.total) : NaN;
  const limitFromApi =
    paginationMeta.limit != null && Number(paginationMeta.limit) > 0
      ? Number(paginationMeta.limit)
      : requestLimit;
  if (!Number.isNaN(total) && total >= 0 && limitFromApi > 0) {
    return Math.max(1, Math.ceil(total / limitFromApi));
  }
  return 1;
}

const TaskAttendanceHistory = () => {
  const { t } = useTranslation();
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor } = useTheme();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [currentPage, setCurrentPage] = useState(1);
  const [historyData, setHistoryData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    workerId: "",
    department: "",
    status: "",
    startDate: "",
    endDate: "",
    groupByDate: false,
    groupByWorker: false,
  });
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const deferredSearch = useDeferredValue(searchTerm.trim());

  const isMobile = useMediaQuery("(max-width:768px)");

  const formatUtcDate = (value) => {
    const parseable = ensureUtcParseableInstant(value);
    if (!parseable) return "—";
    const formatted = convertUTCToLocalDateTime(parseable, {
      locale: "en-GB",
      formatOptions: {
        day: "2-digit",
        month: "short",
        year: "numeric",
      },
    });
    return formatted === "—" ? "—" : formatted;
  };

  /** UTC datetime → local 12-hour time for table/export (single conversion). */
  const formatUtcTime12 = (value) => {
    if (value == null || value === "") return "--";
    const normalized = String(value).trim();
    if (!normalized) return "--";

    if (isTimeOnlyLocalClock(normalized)) {
      const timeOnlyMatch = normalized.match(TIME_ONLY_REGEX);
      const hour = Number(timeOnlyMatch[1]);
      const minute = timeOnlyMatch[2];
      const suffix = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minute} ${suffix}`;
    }

    const formatted = convertUTCToLocalDateTime(normalized, {
      locale: "en-GB",
      formatOptions: {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      },
    });

    return formatted === "—"
      ? "--"
      : formatted.replace(/\u202f|\u00a0/g, " ").trim();
  };

  const getStatusColor = (status) => {
    const statusMap = {
      on_time: "#00C48C",
      late: "#FF974A",
      assigned: "#8F00FF",
      in_progress: "#8E65E9",
      completed: "#00C48C",
      delayed: "#FF974A",
      not_done: "#FF647C",
      cancelled: "#9F4BFF",
      not_started: "#9F4BFF",
      not_ended: "#FFDC60",
    };
    return statusMap[status?.toLowerCase()] || "#6B7280";
  };

  const formatStatus = (status) => {
    if (!status) return "--";
    const statusMap = {
      on_time: t("taskAttendanceHistory.status.onTime"),
      late: t("taskAttendanceHistory.status.late"),
      assigned: t("taskAttendanceHistory.status.assigned"),
      in_progress: t("taskAttendanceHistory.status.inProgress"),
      completed: t("taskAttendanceHistory.status.completed"),
      delayed: t("taskAttendanceHistory.status.delayed"),
      not_done: t("taskAttendanceHistory.status.notDone"),
      cancelled: t("taskAttendanceHistory.status.cancelled"),
      not_started: t("taskAttendanceHistory.status.notStarted"),
      not_ended: t("taskAttendanceHistory.status.notEnded"),
    };
    return statusMap[status?.toLowerCase()] || status;
  };

  const formatDelay = (delayMinutes, status) => {
    if (!delayMinutes || delayMinutes === 0 || status === "on_time" || status === "not_started" || status === "not_ended") {
      return "--";
    }
    if (delayMinutes < 60) {
      return `${delayMinutes} ${delayMinutes === 1 ? t("taskAttendanceHistory.minute") : t("taskAttendanceHistory.minutes")}`;
    }
    const hours = Math.floor(delayMinutes / 60);
    const minutes = delayMinutes % 60;
    if (minutes === 0) {
      return `${hours} ${hours === 1 ? t("taskAttendanceHistory.hour") : t("taskAttendanceHistory.hours")}`;
    }
    return `${hours}h ${minutes}m`;
  };

  // Extract location data from events
  const getLocationData = (task) => {
    const location = task.location || {};
    const events = task.events || [];

    const startEvent = events.find(e => e.event_type === "start");
    const endEvent = events.find(e => e.event_type === "end");

    // Convert distance values to numbers if they exist
    const startDistance = startEvent?.distance_m;
    const endDistance = endEvent?.distance_m;

    return {
      taskLocation: location.address || "--",
      taskLat: location.lat,
      taskLng: location.lng,
      radiusM: location.radius_m || 100,
      locationRequired: task.requirements?.location_required || false,
      startLocationValidated: startEvent?.location_validated || false,
      startDistanceM: startDistance != null ? Number(startDistance) : null,
      startGeoLat: startEvent?.geo_lat || null,
      startGeoLng: startEvent?.geo_lng || null,
      endLocationValidated: endEvent?.location_validated || false,
      endDistanceM: endDistance != null ? Number(endDistance) : null,
      endGeoLat: endEvent?.geo_lat || null,
      endGeoLng: endEvent?.geo_lng || null,
    };
  };

  const mapAttendanceTaskRow = (task, { worker, dateGroupDate } = {}) => {
    const locationData = getLocationData(task);
    const dateInstant = resolveTaskDateInstant(task, dateGroupDate);

    return {
      id: task.task_id,
      workerName: worker?.name || task.worker?.name || "--",
      workerEmail: worker?.email || task.worker?.email || "--",
      workerDepartment: worker?.department || task.worker?.department || "--",
      taskName: task.title || "--",
      date: formatUtcDate(dateInstant),
      scheduledStartTime: formatUtcTime12(task.scheduled?.start_at),
      scheduledEndTime: formatUtcTime12(task.scheduled?.end_at),
      actualStartTime: formatUtcTime12(task.actual?.started_at),
      actualEndTime: formatUtcTime12(task.actual?.ended_at),
      startStatus: task.attendance?.start_status || "--",
      endStatus: task.attendance?.end_status || "--",
      startDelayMinutes: task.attendance?.start_delay_minutes || 0,
      endDelayMinutes: task.attendance?.end_delay_minutes || 0,
      startDelay: formatDelay(
        task.attendance?.start_delay_minutes || 0,
        task.attendance?.start_status
      ),
      endDelay: formatDelay(
        task.attendance?.end_delay_minutes || 0,
        task.attendance?.end_status
      ),
      ...locationData,
      startTime: formatUtcTime12(task.actual?.started_at),
      endTime: formatUtcTime12(task.actual?.ended_at),
      status: task.assignment_status || task.task_status || "--",
      raw: task,
      ...(worker ? { workerRaw: worker } : {}),
    };
  };

  useEffect(() => {
    const isFirstLoad = initialLoading;
    if (!isFirstLoad) setTableLoading(true);

    let url = `${BASE_URL}/task-management/admin/tasks/attendance-history?limit=${ROWS_PER_PAGE}&page=${currentPage}`;

    if (deferredSearch) {
      url += `&search=${encodeURIComponent(deferredSearch)}`;
    }

    if (filters.workerId) url += `&workerId=${filters.workerId}`;
    if (filters.department) url += `&department=${filters.department}`;
    if (filters.status) url += `&status=${filters.status}`;
    if (filters.startDate) {
      url += `&dateFrom=${encodeURIComponent(filters.startDate)}`;
    }
    if (filters.endDate) {
      url += `&dateTo=${encodeURIComponent(filters.endDate)}`;
    }
    if (filters.groupByDate) url += `&group_by_date=true`;
    if (filters.groupByWorker) url += `&group_by_worker=true`;

    console.log("📡 Fetching task attendance history with URL:", url);

    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("✅ Task Attendance History API response:", data);

        if (!data.error && data.data) {
          let mapped = [];

          // Handle grouped data
          if (filters.groupByWorker && data.data.workers) {
            mapped = data.data.workers.flatMap((worker) =>
              (worker.tasks || []).map((task) =>
                mapAttendanceTaskRow(task, { worker })
              )
            );
          } else if (filters.groupByDate && data.data.dates) {
            mapped = data.data.dates.flatMap((dateGroup) =>
              (dateGroup.tasks || []).map((task) =>
                mapAttendanceTaskRow(task, { dateGroupDate: dateGroup.date })
              )
            );
          } else if (data.data.tasks) {
            mapped = (data.data.tasks || []).map((task) => mapAttendanceTaskRow(task));
          }

          setHistoryData(mapped);
          const p = getPaginationFromResponse(data);
          setTotalPages(resolveTotalPageCount(p, ROWS_PER_PAGE));
        } else {
          setHistoryData([]);
          setTotalPages(1);
        }
      })
      .catch((err) => {
        console.error("❌ Error fetching task attendance history:", err);
        setHistoryData([]);
      })
      .finally(() => {
        if (isFirstLoad) {
          setInitialLoading(false);
        } else {
          setTableLoading(false);
        }
      });
  }, [currentPage, decryptedToken, filters, deferredSearch]);

  const HeaderStyles = {
    color: "#44546F",
    fontSize: "15px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const DataStyles = {
    color: "#172B4D",
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const handleApplyFilters = (newFilters) => {
    console.log("Applied Filters:", newFilters);
    setFilters(newFilters);
    setCurrentPage(1);
    setIsFilterApplied(
      !!(
        newFilters.workerId ||
        newFilters.department ||
        newFilters.status ||
        newFilters.startDate ||
        newFilters.endDate ||
        newFilters.groupByDate ||
        newFilters.groupByWorker
      )
    );
  };

  const clearFilters = () => {
    const cleared = {
      workerId: "",
      department: "",
      status: "",
      startDate: "",
      endDate: "",
      groupByDate: false,
      groupByWorker: false,
    };
    setFilters(cleared);
    setCurrentPage(1);
    setIsFilterApplied(false);
  };

  const { logoBase64 } = useCompanyProfile(decryptedToken);

  // Fetch all task attendance history for export
  const fetchAllTaskAttendanceHistory = async (decryptedToken, filters = {}, searchQuery = "") => {
    try {
      let url = `${BASE_URL}/task-management/admin/tasks/attendance-history?no_pagination=true`;

      const q = String(searchQuery || "").trim();
      if (q) url += `&search=${encodeURIComponent(q)}`;

      if (filters.workerId) url += `&workerId=${filters.workerId}`;
      if (filters.department) url += `&department=${filters.department}`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.startDate) {
        url += `&dateFrom=${encodeURIComponent(filters.startDate)}`;
      }
      if (filters.endDate) {
        url += `&dateTo=${encodeURIComponent(filters.endDate)}`;
      }
      if (filters.groupByDate) url += `&group_by_date=true`;
      if (filters.groupByWorker) url += `&group_by_worker=true`;

      console.log("📡 Task Attendance Export API URL:", url);

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      console.log("✅ Task Attendance Export API Response:", data);

      if (!data.error && data.data) {
        let mapped = [];

        // Handle grouped data
        if (filters.groupByWorker && data.data.workers) {
          mapped = data.data.workers.flatMap((worker) =>
            (worker.tasks || []).map((task) =>
              mapAttendanceTaskRow(task, { worker })
            )
          );
        } else if (filters.groupByDate && data.data.dates) {
          mapped = data.data.dates.flatMap((dateGroup) =>
            (dateGroup.tasks || []).map((task) =>
              mapAttendanceTaskRow(task, { dateGroupDate: dateGroup.date })
            )
          );
        } else if (data.data.tasks) {
          mapped = (data.data.tasks || []).map((task) => mapAttendanceTaskRow(task));
        }

        return mapped;
      } else {
        return [];
      }
    } catch (err) {
      console.error("❌ Error fetching task attendance history for export:", err);
      return [];
    }
  };

  return (
    <>
      <Box sx={{ backgroundColor: "white", borderRadius: 2, border: "2px solid #091E4224", py: 2, m: 1 }}>
        {/* Header */}
        <Box
          sx={{
            pb: 2,
            px: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CustomText sx={{ ...textStyles.h1, fontWeight: 600, fontSize: "18px", color: "#003149" }}>
              {t("taskAttendanceHistory.heading")}
            </CustomText>
            {tableLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Box>

          {/* Search */}
          <Box
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: 1,
              height: 34,
              border: "2px solid #091E4224",
              display: "flex",
              alignItems: "center",
              minWidth: 250,
              maxWidth: 300,
            }}
          >
            <InputBase
              placeholder={t("taskAttendanceHistory.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              sx={{
                ml: 0.5,
                fontSize: 15,
                fontFamily: "Poppins, sans-serif",
                width: "100%",
              }}
            />
            <SearchIcon sx={{ fontSize: 22, color: "#888", mr: 0.5 }} />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ExportDropdown
              onDownloadPDF={async () => {
                const allData = await fetchAllTaskAttendanceHistory(decryptedToken, filters, searchTerm);
                const blob = await pdf(<ExportTaskAttendanceHistoryPDF data={allData} logo={logoBase64} />).toBlob();
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "Task_Attendance_History.pdf";
                link.click();
              }}
              onDownloadExcel={async () => {
                const allData = await fetchAllTaskAttendanceHistory(decryptedToken, filters, searchTerm);
                ExportTaskAttendanceHistoryExcel(allData);
              }}
            />
            {isFilterApplied ? (
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 34,
                  height: 34,
                  borderRadius: 1,
                  bgcolor: primaryColor,
                }}
              >
                <FilterAltIcon sx={{ color: primaryTextColor, fontSize: 20 }} />
                <IconButton
                  size="small"
                  onClick={clearFilters}
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    bgcolor: "white",
                    width: 18,
                    height: 18,
                    border: "1px solid #C4C4C4",
                    "&:hover": {
                      bgcolor: `${secondaryColor}10`,
                      borderColor: secondaryColor,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 14, color: secondaryTextColor }} />
                </IconButton>
              </Box>
            ) : (
              <IconButton
                onClick={() => setFilterOpen(true)}
                sx={{
                  border: "1px solid #C4C4C4",
                  borderRadius: 1,
                  width: 34,
                  height: 34,
                  "&:hover": {
                    backgroundColor: `${primaryColor}10`,
                    borderColor: primaryColor,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <FilterAltIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Table */}
        {initialLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
            <CircularProgress />
          </Box>
        ) : historyData.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 5,
            }}
          >
            <img src={empty} alt="No Data" style={{ width: 200, marginBottom: 16 }} />
            <Typography sx={{ fontSize: "15px" }}>{t("empty.no_user")}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
              <Table>
                <TableHead
                  sx={{
                    borderTop: "2px solid #091E4224",
                    borderBottom: "2px solid #091E4224",
                  }}
                >
                  <TableRow>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.workerName")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.workerEmail")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.department")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.taskName")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.date")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.taskLocation")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.scheduledStart")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.actualStart")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.startStatus")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.startDelay")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.startLocationValidated")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.startDistance")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.scheduledEnd")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.actualEnd")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.endStatus")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.endDelay")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.endLocationValidated")}</TableCell>
                    <TableCell sx={HeaderStyles}>{t("taskAttendanceHistory.tableHeaders.endDistance")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((row) => (
                    <TableRow key={row.id ?? `${row.workerEmail}-${row.date}-${row.taskName}`} hover sx={{ cursor: "pointer" }}>
                      <TableCell sx={DataStyles}>{row.workerName}</TableCell>
                      <TableCell sx={DataStyles}><TruncatedCell text={row.workerEmail} /></TableCell>
                      <TableCell sx={DataStyles}><TruncatedCell text={row.workerDepartment} /></TableCell>
                      <TableCell sx={DataStyles}><TruncatedCell text={row.taskName} /></TableCell>
                      <TableCell sx={DataStyles}>{row.date}</TableCell>
                      {/* Task Location */}
                      <TableCell sx={DataStyles}>
                        <TruncatedCell text={row.taskLocation || "--"} />
                        {row.locationRequired && (
                          <Typography sx={{ fontSize: "10px", color: "#666", mt: 0.5 }}>
                            {t("taskAttendanceHistory.locationRequired")}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Scheduled Start */}
                      <TableCell sx={DataStyles}>{row.scheduledStartTime}</TableCell>
                      {/* Actual Start */}
                      <TableCell sx={DataStyles}>{row.actualStartTime}</TableCell>
                      {/* Start Status */}
                      <TableCell sx={DataStyles}>
                        {row.startStatus && row.startStatus !== "--" ? (
                          <Chip
                            label={formatStatus(row.startStatus)}
                            sx={{
                              backgroundColor: getStatusColor(row.startStatus),
                              color: "white",
                              fontSize: "12px",
                              fontWeight: 500,
                              fontFamily: "Poppins, sans-serif",
                              height: "26px",
                              borderRadius: "4px",
                            }}
                          />
                        ) : (
                          "--"
                        )}
                      </TableCell>
                      {/* Start Delay */}
                      <TableCell sx={DataStyles}>{formatDelay(row.startDelayMinutes, row.startStatus)}</TableCell>
                      {/* Start Location Validated */}
                      <TableCell sx={DataStyles}>
                        {row.locationRequired ? (
                          row.startLocationValidated !== undefined ? (
                            <Chip
                              label={row.startLocationValidated ? t("taskAttendanceHistory.validated") : t("taskAttendanceHistory.notValidated")}
                              sx={{
                                backgroundColor: row.startLocationValidated ? "#00C48C" : "#FF647C",
                                color: "white",
                                fontSize: "11px",
                                fontWeight: 500,
                                fontFamily: "Poppins, sans-serif",
                                height: "24px",
                                borderRadius: "4px",
                              }}
                            />
                          ) : (
                            "--"
                          )
                        ) : (
                          <Typography sx={{ fontSize: "11px", color: "#999" }}>
                            {t("taskAttendanceHistory.notRequired")}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Start Distance */}
                      <TableCell sx={DataStyles}>
                        {row.startDistanceM !== null && row.startDistanceM !== undefined && typeof row.startDistanceM === 'number' ? (
                          `${Number(row.startDistanceM).toFixed(1)}m`
                        ) : (
                          "--"
                        )}
                      </TableCell>
                      {/* Scheduled End */}
                      <TableCell sx={DataStyles}>{row.scheduledEndTime}</TableCell>
                      {/* Actual End */}
                      <TableCell sx={DataStyles}>{row.actualEndTime}</TableCell>
                      {/* End Status */}
                      <TableCell sx={DataStyles}>
                        {row.endStatus && row.endStatus !== "--" ? (
                          <Chip
                            label={formatStatus(row.endStatus)}
                            sx={{
                              backgroundColor: getStatusColor(row.endStatus),
                              color: "white",
                              fontSize: "12px",
                              fontWeight: 500,
                              fontFamily: "Poppins, sans-serif",
                              height: "26px",
                              borderRadius: "4px",
                            }}
                          />
                        ) : (
                          "--"
                        )}
                      </TableCell>
                      {/* End Delay */}
                      <TableCell sx={DataStyles}>{formatDelay(row.endDelayMinutes, row.endStatus)}</TableCell>
                      {/* End Location Validated */}
                      <TableCell sx={DataStyles}>
                        {row.locationRequired ? (
                          row.endLocationValidated !== undefined ? (
                            <Chip
                              label={row.endLocationValidated ? t("taskAttendanceHistory.validated") : t("taskAttendanceHistory.notValidated")}
                              sx={{
                                backgroundColor: row.endLocationValidated ? "#00C48C" : "#FF647C",
                                color: "white",
                                fontSize: "11px",
                                fontWeight: 500,
                                fontFamily: "Poppins, sans-serif",
                                height: "24px",
                                borderRadius: "4px",
                              }}
                            />
                          ) : (
                            "--"
                          )
                        ) : (
                          <Typography sx={{ fontSize: "11px", color: "#999" }}>
                            {t("taskAttendanceHistory.notRequired")}
                          </Typography>
                        )}
                      </TableCell>
                      {/* End Distance */}
                      <TableCell sx={DataStyles}>
                        {row.endDistanceM !== null && row.endDistanceM !== undefined && typeof row.endDistanceM === 'number' ? (
                          `${Number(row.endDistanceM).toFixed(1)}m`
                        ) : (
                          "--"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                shape="rounded"
                color="primary"
                size="small"
                disabled={tableLoading}
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontFamily: "Poppins, sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                  },
                  "& .Mui-selected": {
                    backgroundColor: `${primaryColor}10`,
                    color: primaryColor,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>

      <TaskAttendanceHistoryFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        setFilters={setFilters}
        setIsFilterApplied={setIsFilterApplied}
        onApplyFilters={handleApplyFilters}
      />
    </>
  );
};

export default TaskAttendanceHistory;
